import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, MessageSquareText, ShieldCheck, ArrowLeft, Copy, Pencil, RotateCcw, StickyNote, Save,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const CATEGORY_STYLES = {
  'Issue Tracker': 'bg-blue-50 text-blue-800 border-blue-300',
  Contact: 'bg-amber-50 text-amber-900 border-amber-300',
  Volunteer: 'bg-emerald-50 text-emerald-800 border-emerald-300',
};

const TemplateCard = ({ t, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: t.title, copy: t.copy });
  const [busy, setBusy] = useState(false);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(t.copy || '');
      toast.success('Template copied to clipboard');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = t.copy || '';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Template copied to clipboard');
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/first-response-templates/${t.id}`, form);
      toast.success('Template saved');
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/first-response-templates/${t.id}/restore`);
      toast.success('Default wording restored');
      setEditing(false);
      onSaved();
    } catch { toast.error('Restore failed'); }
    setBusy(false);
  };

  const saveNotes = async (notes) => {
    if (notes === (t.notes || '')) return;
    try {
      await api.put(`/admin/first-response-templates/${t.id}`, { notes });
      toast.success('Notes saved');
      onSaved();
    } catch { toast.error('Notes save failed'); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`template-${t.key}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-serif font-bold text-[#1a2744] text-sm leading-snug">{t.title}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Last updated {String(t.updated_at || '').slice(0, 10)}</p>
        </div>
        <Badge variant="outline" className={`text-[10px] font-bold shrink-0 ${CATEGORY_STYLES[t.category] || 'bg-slate-50 text-slate-600 border-slate-300'}`} data-testid={`template-category-${t.key}`}>{t.category}</Badge>
      </div>

      {editing ? (
        <div className="mt-3">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Title</label>
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="h-10 text-sm font-bold mt-1 mb-3" data-testid={`template-title-input-${t.key}`} />
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Reply copy</label>
          <Textarea value={form.copy} onChange={(e) => setForm((f) => ({ ...f, copy: e.target.value }))} rows={10} className="text-sm mt-1" data-testid={`template-copy-input-${t.key}`} />
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" onClick={save} disabled={busy} className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`template-save-${t.key}`}>
              <Save className="h-3.5 w-3.5 mr-1" /> Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setForm({ title: t.title, copy: t.copy }); }} className="h-9 text-xs font-bold btn-press" data-testid={`template-cancel-${t.key}`}>Cancel</Button>
          </div>
        </div>
      ) : (
        <pre className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto" data-testid={`template-copy-${t.key}`}>{t.copy}</pre>
      )}

      {!editing && (
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" onClick={copyText} className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`template-copy-btn-${t.key}`}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy Reply
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-9 text-xs font-bold btn-press" data-testid={`template-edit-btn-${t.key}`}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
          <Button size="sm" variant="outline" onClick={restore} disabled={busy} className="h-9 text-xs font-bold btn-press" data-testid={`template-restore-btn-${t.key}`}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore Default
          </Button>
        </div>
      )}

      <div className="mt-3">
        <label className="text-[11px] font-bold text-slate-500 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Notes</label>
        <Input defaultValue={t.notes || ''} onBlur={(e) => saveNotes(e.target.value)} placeholder="e.g. Use for Facebook comments too…" className="h-10 text-sm mt-1" data-testid={`template-notes-${t.key}`} />
      </div>
    </div>
  );
};

export default function AdminFirstResponses() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    api.get('/admin/first-response-templates').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="templates-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the First Response Templates.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-first-responses">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><MessageSquareText className="h-6 w-6 text-[#C9A84C]" /> First Response Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Ready-to-copy replies for the first issue reports, contact messages, and volunteer signups after launch.</p>
        </div>
        <Button asChild variant="outline" className="h-11 font-bold btn-press shrink-0" data-testid="templates-back-launch-btn">
          <Link to="/admin/launch-content"><ArrowLeft className="h-4 w-4 mr-1.5" /> Launch Content</Link>
        </Button>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-5" data-testid="templates-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      <div className="space-y-3">
        {data.templates.map((t) => <TemplateCard key={t.id} t={t} onSaved={load} />)}
      </div>
    </div>
  );
}
